/* @flow */
import moment from 'moment';
import _ from 'lodash';

import keys from '../../config/keys';

import type {
  LiferayError,
  Category,
  SimplifiedMedia,
  Media,
  TrackMedia,
  CoverSize,
  Cover,
  Person,
  Track,
  TrackAggregationKey,
  MediaAggregationKey,
  Aggregation,
  Aggregations,
  Cut,
  AudioResourceSet,
  Opera,
  Catalog,
  Workcopies,
  Bucket,
  Production,
  Producer,
  SimplifiedUsage,
  Usage,
  UsageClass,
  Territory,
  UsageType,
  Area,
  Channel,
  Broadcasting,
  Platform,
  BroadcastType,
  ProductionType,
  Worklist,
  SimplifiedWorklist,
  ExtendedWorklist,
  WorklistItem,
  CartItem,
  Smartlist,
  SmartlistItem,
  Featured,
  Purchase,
  OnDemand,
  MailRequest,
  LicenseComponent,
  MergedLicenseComponent,
} from '../../types';

const toError = (error: any): LiferayError => {
  const { exception, message } = error;
  switch (exception) {
    case 'com.api.portal.security.auth.PrincipalException': {
      return {
        code: 'PRINCIPAL',
        message: 'Azione non consentita',
      };
    }
    default: {
      return {
        code: 'GENERIC',
        message: keys.IS_ENV_DEVELOPMENT
          ? `${exception}: ${message}`
          : message.replace(/^it.protones.[^\s]*\s*/, ''),
      };
    }
  }
};

const toCategory = (categoria: any): Category => ({
  id: categoria.categoriaId,
  uuid: categoria.uuid,
  parentId: categoria.categoriaMadreId !== 0 ? categoria.categoriaMadreId : undefined,
  description: categoria.descrizione,
  extendedDescription: categoria.descrizioneEstesa,
});

const toCover = (covers: any, size: CoverSize): Cover => {
  const cover = covers[size];
  const coverDimensions = {
    SMALL: {
      width: 34,
      height: 34,
    },
    MEDIUM: {
      width: 64,
      height: 64,
    },
    LARGE: {
      width: 174,
      height: 174,
    },
    EXTRALARGE: {
      width: 300,
      height: 300,
    },
    MEGA: {},
  };
  return {
    url: `${keys.STATIC_ASSETS_URL}/${cover.sourceParametri}`,
    type: cover.tipo,
    ...coverDimensions[size],
    size,
  };
};

const toPerson = (interprete: any): Person => ({
  id: interprete.id,
  anonymous: interprete.anonimato,
  fullName: interprete.nomeCompleto,
  publicName: interprete.nomePubblico,
  firstName: interprete.nome,
  lastName: interprete.cognome,
  group: interprete.gruppo,
  alias: interprete.pseudonimo,
});

const toSimplifiedMedia = (supporto: any): SimplifiedMedia => {
  let hit = supporto;
  return {
    id: hit.id, // 79072,
    title: hit.titolo, // titolo: 'The Party (this Is How We Do It)',
    categories:
      hit.categorie &&
      hit.categorie.map(categoria => ({
        id: categoria.id,
        description: categoria.descrizione,
      })),
    label:
      (hit.etichetta && {
        id: hit.etichetta.id,
        name: hit.etichetta.nomePubblico,
      }) ||
      undefined,
    year: hit.anno || undefined,
    availableTracksCount: hit.numeroTracceAvailable,
    availableTracksDuration: hit.durataTotaleTracceAvailable,
    creationDate: hit.dataCreazione,
    covers: [
      toCover(hit.cover, 'SMALL'),
      toCover(hit.cover, 'MEDIUM'),
      toCover(hit.cover, 'LARGE'),
      toCover(hit.cover, 'EXTRALARGE'),
      toCover(hit.cover, 'MEGA'),
    ],
    performers: hit.interpreti && hit.interpreti.map(toPerson),
  };
};

const toAudioResourceSet = (traccia: any): AudioResourceSet => {
  const result: AudioResourceSet = {};
  if (traccia.sdAudioFileId) {
    result.sd = {
      fileId: traccia.sdAudioFileId,
      url: traccia.sdAudioFileId,
    };
  }
  if (traccia.hdAudioFileId) {
    result.hd = {
      fileId: traccia.hdAudioFileId,
      url: traccia.hdAudioFileId,
    };
  }
  return result;
};

const toCut = (taglio: any): Cut => ({
  id: taglio.id,
  title: taglio.titolo,
  type: taglio.tipo,
  duration: taglio.durata,
  resources: toAudioResourceSet(taglio),
});

const toOpera = (opera: any): Opera => ({
  id: opera.id,
  catalogId: opera.idCatalogo,
  iswc: opera.ISWC,
  isPublicDomain: opera.isPubblicoDominio,
  code: opera.codiceOpera,
  title: opera.titolo,
  subtitle: opera.sottotitolo,
});

const toCatalog = (catalogo: any): Catalog => ({
  id: catalogo.id,
  code: catalogo.codice,
  description: catalogo.descrizione,
});

const toWorkcopies = (supporto: any): Workcopies => {
  return {
    opera:
      supporto.operaWorkcopy &&
      supporto.operaWorkcopy.map(x => ({
        rightId: x.tipoDirittoId,
        collectings: x.collectings,
      })),
    recording:
      supporto.registrazioneWorkcopy &&
      supporto.registrazioneWorkcopy.map(x => ({
        rightId: x.tipoDirittoId,
        collectings: x.collectings,
      })),
  };
};

const toBucket = (bucket: any): Bucket => ({
  key: bucket.key,
  count: bucket.doc_count,
});

const toBasicAggregation = (aggregation: any): Aggregation => ({
  docCount: _.get(aggregation, 'doc_count'),
  docCountErrorUpperBound: _.get(aggregation, 'doc_count_error_upper_bound'),
  others: _.get(aggregation, 'sum_other_doc_count'),
  buckets: _.get(aggregation, 'buckets', []).map(toBucket),
  totalBuckets: 0,
});

const toMediaAggregations = (aggregations: any): Aggregations<MediaAggregationKey> => ({
  categories: toBasicAggregation(aggregations.categorie),
  catalogs: toAggregation(aggregations.cataloghi),
  collectings: toAggregation(aggregations.collectings),
  tags: toAggregation(aggregations.tags),
  labels: toAggregation(aggregations.etichette),
  producers: toAggregation(aggregations.produttori),
  performers: toAggregation(aggregations.interpreti),
  editors: toAggregation(aggregations.editori),
  authors: toAggregation(aggregations.autori),
  years: toAggregation(aggregations.anno),
});

const toAggregation = (aggregation: any): Aggregation => ({
  docCount: _.get(aggregation, 'doc_count', 0),
  docCountErrorUpperBound: _.get(aggregation, 'values.doc_count_error_upper_bound'),
  others: _.get(aggregation, 'values.sum_other_doc_count'),
  buckets: _.get(aggregation, 'values.buckets', []).map(toBucket),
  totalBuckets: _.get(aggregation, 'total_buckets.value'),
});

const toTrackAggregations = (aggregations: any): Aggregations<TrackAggregationKey> => ({
  catalogs: toAggregation(aggregations.cataloghi),
  collectings: toAggregation(aggregations.collectings),
  tags: toAggregation(aggregations.tags),
  labels: toAggregation(aggregations.etichette),
  categories: toAggregation(aggregations.categorie),
  producers: toAggregation(aggregations.produttori),
  performers: toAggregation(aggregations.interpreti),
  editors: toAggregation(aggregations.editori),
  authors: toAggregation(aggregations.autori),
  years: toAggregation(aggregations.anno),
});

const toTrack = (track: any): Track => {
  const hit = track;
  return {
    id: hit.id,
    tags: hit.tags,
    price: hit.price ? Number(hit.price) : undefined,
    bpm: hit.bpm,
    metre: hit.metro,
    orchestration: hit.orchestrazione,
    performers: hit.interpreti ? hit.interpreti.map(toPerson) : undefined,
    authors: hit.autori ? hit.autori.map(toPerson) : undefined,
    duration: hit.durata,
    tempo: hit.tempo,
    genre: hit.genere,
    title: hit.titolo,
    cuts: hit.tagli ? hit.tagli.map(toCut) : undefined,
    downloadable: hit.downloadable,
    usages: hit.utilizzi,
    tones: hit.tonalita,
    opera: hit.opera ? toOpera(hit.opera) : undefined,
    medias: (hit.supporti || []).map(s => toSimplifiedMedia(s._source || s)), // TODO
    catalog: hit.catalogo ? toCatalog(hit.catalogo) : undefined,
    resources: toAudioResourceSet(hit),
  };
};

const toSimplifiedUsage = (usageBox: any): SimplifiedUsage => ({
  id: usageBox.usageBoxId, //  "usageBoxId": 8401,
  name: usageBox.nome, // "nome": "sottofondo per intrattenimento"
});

const toTerritory = (territorio: any): Territory => ({
  id: territorio.territorioId, // "territorioId": 1,
  name: territorio.descrizione, // "descrizione": "Italia",
  uuid: territorio.uuid, // "uuid": "592afe96-27f5-4c58-9164-461b677558f5"
});

const toUsageType = (tipologiaUtilizzo: any): UsageType => ({
  id: tipologiaUtilizzo.id, // "territorioId": 1,
  name: tipologiaUtilizzo.name, // "descrizione": "Italia",
  uuid: tipologiaUtilizzo.uuid, // "uuid": "592afe96-27f5-4c58-9164-461b677558f5"
});

const toProducer = (produttore: any): Producer => ({
  createDate: produttore.createDate,
  creationDate: produttore.creationDate,
  modificationDate: produttore.modificationDate,
  modifiedDate: produttore.modifiedDate,
  name: produttore.name,
  id: produttore.id,
  fullName: produttore.fullName,
  uuid: produttore.uuid,
});

const toProduction = (produzione: any): Production => ({
  id: produzione.produzioneId, // "usageBoxId": 1,
  uuid: produzione.uuid, // "uuid": "46d06fca-0c4e-4f3d-ac36-c88210651bb5"
  createDate: produzione.createDate, // "createDate": 1459942491144,
  modifiedDate: produzione.modifiedDate, // "modifiedDate": 1459942491144,
  name: produzione.nomeProduzione, // "nomeProduzione": "Cielo che goal",
  edition: produzione.edizione, // "edizione": "",
  quantity: produzione.quantitaProduzione, // "quantitaProduzione": -1,
  area:
    produzione.areaId !== 0
      ? {
          id: produzione.areaId, // "areaId": -1,
          name: produzione.area, // "area": null,
        }
      : undefined,
  producer:
    produzione.produttoreId !== 0
      ? {
          id: produzione.produttoreId, // "produttoreId": 0,
          name: produzione.produttore, // "produttore": "produttore 1",
        }
      : undefined,
  productionType:
    produzione.tipologiaProduzioneId !== 0
      ? {
          id: produzione.tipologiaProduzioneId, // "tipologiaProduzioneId": 2,
          name: produzione.tipologiaProduzione, // "tipologiaProduzione": "Prodotto TV - Intrattenimento",
        }
      : undefined,
});

const toUsageClass = (tipo: number): UsageClass | void => {
  switch (tipo) {
    case 220000:
      return 'USER';
    case 220001:
      return 'SYSTEM';
    case 220002:
      return 'WORKLIST';
    default:
      return undefined;
  }
};

// const usageClassToInt = (type: UsageClass): number | void => {
//   switch (type) {
//     case 'USER':
//       return 220000;
//     case 'SYSTEM':
//       return 220001;
//     case 'WORKLIST':
//       return 220002;
//     default:
//       console.warn('type =', type);
//       return undefined;
//   }
// };

const toUsage = (usageBox: any): Usage => ({
  id: usageBox.id, // "usageBoxId": 4701,
  uuid: usageBox.uuid, // "uuid": "b4acf94e-84f5-494d-8db6-be9b369900da"
  status: usageBox.status, // "status": false,
  statusLocked: usageBox.statusLocked, // "statusLocked": false,
  createDate: usageBox.createDate, // "createDate": 1475168601424,
  modifiedDate: usageBox.modifiedDate, // "modifiedDate": 1475168601424,
  name: usageBox.nome, // "nome": "sottofondo per intrattenimento",
  startDate: usageBox.startDate, // "dataInizioUtilizzo": null,
  startDateLocked: usageBox.startDateLocked, // "dataInizioUtilizzoLocked": false,
  endDate: usageBox.endDate, // "dataFineUtilizzo": null,
  endDateLocked: usageBox.endDateLocked, // "dataFineUtilizzoLocked": false,
  exclusive: usageBox.esclusiva, // "esclusiva": false,
  exclusiveLocked: usageBox.esclusivaLocked, // "esclusivaLocked": false,
  exclusiveEndDate: usageBox.dataFineEsclusiva, // "dataFineEsclusiva": 1475168601424,
  exclusiveEndDateLocked: usageBox.dataFineEsclusivaLocked, // dataFineEsclusivaLocked: boolean,
  exclusiveNotes: usageBox.estremiEsclusiva, // "estremiEsclusiva": "",
  exclusiveNotesLocked: usageBox.estremiEsclusivaLocked,
  notes: usageBox.notes, // "note": false,
  notesLocked: usageBox.notesLocked, // "noteLocked": false,
  production: usageBox.production ? toProduction(usageBox.production) : undefined, // "produzione": {
  productionLocked: usageBox.productionLocked, // "produzioneIdLocked": false
  episode: usageBox.episode, // "puntata": null,
  episodeLocked: usageBox.episodeLocked, // "puntataLocked": false,
  channel:
    usageBox.channel.canaleId !== 0
      ? {
          id: usageBox.channel.canaleId, // "canaleId": 43,
          name: usageBox.channel.nome, // "canale": "Sky Cinema Cult",
        }
      : undefined,
  channelLocked: usageBox.channelLocked, // "canaleIdLocked": false,
  broadcasting:
    usageBox.broadcasting.id !== 0
      ? {
          id: usageBox.broadcasting.id, // "emittenteId": 89262,
          name: usageBox.broadcasting.name, // "emittente": "SKY",
          fullName: usageBox.broadcasting.fullName,
          type: usageBox.broadcasting.type,
          peopleId: usageBox.broadcasting.peopleId,
          organizationId: usageBox.broadcasting.organizationId,
          enteId: usageBox.broadcasting.enteId,
        }
      : undefined,
  broadcastingLocked: usageBox.broadcastingLocked, // "emittenteIdLocked": false,
  platform:
    usageBox.platform.piattaformaId !== 0
      ? {
          id: usageBox.platform.piattaformaId, // "piattaformaId": 801,
          name: usageBox.platform.nome, // "piattaforma": "satellite",
        }
      : undefined,
  platformLocked: usageBox.platformLocked, // "piattaformaIdLocked": false,
  broadcastType:
    usageBox.broadcastType.id !== 0
      ? {
          id: usageBox.broadcastType.id, // "tipologiaEmissioneId": 2,
          name: usageBox.broadcastType.name, // "tipologiaEmissione": null,
        }
      : undefined,
  broadcastTypeLocked: usageBox.broadcastTypeLocked, // "tipologiaEmissioneIdLocked": false,
  territory:
    usageBox.territory.territorioId !== 0
      ? {
          id: usageBox.territory.territorioId, // "territorioId": 1,
          name: usageBox.territory.descrizione, // "territorio": "Italia",
        }
      : undefined,
  territoryLocked: usageBox.territoryLocked, // "territorioIdLocked": false,
  usageType:
    usageBox.usageType.id !== 0
      ? {
          id: usageBox.usageType.id, // "tipologiaUtilizzoId": 3,
          name: usageBox.usageType.name, // "tipologiaUtilizzo": "Sottofondo",
        }
      : undefined,
  usageTypeLocked: usageBox.usageTypeLocked, // "tipologiaUtilizzoIdLocked": false,
  type: toUsageClass(usageBox.tipoId),
  updatable: usageBox.updatable,
});

const toArea = (userGroup: any): Area => ({
  // addedByLDAPImport: boolean, //  "addedByLDAPImport": false,
  // companyId: number, //  "companyId": 20155,
  createDate: userGroup.createDate, //  "createDate": 1458726258880,
  description: userGroup.description, //  "description": "",
  modifiedDate: userGroup.modifiedDate, //  "modifiedDate": 1458726258880,
  name: userGroup.name, //  "name": "DDR CANALE5",
  // parentUserGroupId: number, //  "parentUserGroupId": 0,
  id: userGroup.areaId, //  "userGroupId": 54968,
  // userId: number, //  "userId": 20222,
  // userName: string, //  "userName": "Test Test",
  uuid: userGroup.uuid, //  "uuid": "60d24f1c-d098-4ef1-b68a-0513c99de446"
});

const toPlatform = (piattaforma: any): Platform => ({
  id: piattaforma.piattaformaId, // "piattaformaId": 801,
  name: piattaforma.nome, // "nome": "satellite",
  uuid: piattaforma.uuid, //   "uuid": "9bd0384b-d8b2-4049-97e4-6c312dec1b9e"
});

const toChannel = (canale: any): Channel => ({
  id: canale.canaleId, //   "canaleId": 4,
  name: canale.nome, //   "nome": "Sky Cinema +1",
  broadcasting:
    canale.emittenteId !== 0
      ? {
          id: canale.emittenteId, //   "emittenteId": 87362,
          name: canale.emittente, //   "emittente": null,
        }
      : undefined,
  platforms: canale.piattaforme.map(toPlatform), //   "piattaforme": [
  uuid: canale.uuid, //   "uuid": "9bd0384b-d8b2-4049-97e4-6c312dec1b9e"
});

const toBroadcasting = (emittente: any): Broadcasting => ({
  id: emittente.organizationId, // "organizationId": 100002,
  uuid: emittente.uuid, // "uuid": "1cb54419-24b7-41de-9d16-9fe90ee01e0e",
  name: emittente.name, // "name": "RTI",
  fullName: emittente.ragioneSociale, // "ragioneSociale": "R.T.I. - RETI TELEVISIVE ITALIANE S.P.A.",
  type: emittente.type, // "type": "emittente"
  peopleId: emittente.peopleId !== 0 ? emittente.peopleId : undefined, // "peopleId": -1,
  // TODO emittenteId === enteId
  organizationId: emittente.organizationId !== 0 ? emittente.organizationId : undefined, // "organizationId": 86070,
  enteId: emittente.enteId !== 0 ? emittente.enteId : undefined, // "enteId": 100002,
});

const toBroadcastType = (tipologiaEmissione: any): BroadcastType => ({
  name: tipologiaEmissione.name, //  "descrizione": "Diretta/Differita",
  id: tipologiaEmissione.id, //  "tipologiaEmissioneId": 1,
  uuid: tipologiaEmissione.uuid || null, // "uuid": "d4ab6338-50b7-4a92-be01-89cbce8aff18"
});

const toProductionType = (tipologiaProduzione: any): ProductionType => ({
  name: tipologiaProduzione.descrizione, //  "descrizione": "Film e Fiction",
  id: tipologiaProduzione.tipologiaProduzioneId, //  "tipologiaProduzioneId": 1,
  uuid: tipologiaProduzione.uuid, // "uuid": "8f69e12a-0481-4c9b-bed5-8bcb4198f80c"
});

const toMergedLicenseComponent = (mergedComponent: any): MergedLicenseComponent => ({
  id: mergedComponent.id,
  description: mergedComponent.description,
  components: mergedComponent.components.map(toLicenseComponent),
});

const toLicenseComponent = (component: any): LicenseComponent => ({
  right: {
    id: component.tipoDirittoId, // tipoDirittoId: 1
    description: component.description, // description: "Comunicazione al pubblico / esecuzione",
    editorial: !!component.editoriale, // editoriale: false
  },
  quotation: component.quotation
    ? {
        createdAt: component.quotation.createDate,
      }
    : undefined,
  discountedPrice: component.discountedPrice ? Number(component.discountedPrice) : undefined, // discountedPrice: null
  // editoriale: component.editoriale, // editoriale: false
  price: component.price ? Number(component.price) : undefined, // price: null
  buyable: !!component.buyable, // buyable: false
});

const toWorklistItem = (worklistItem: any): WorklistItem => ({
  id: worklistItem.worklistItemId,
  uuid: worklistItem.uuid, // "uuid": "b4acf94e-84f5-494d-8db6-be9b369900da"
  price: worklistItem.price ? Number(worklistItem.price) : undefined,
  worklistId: worklistItem.worklistId,
  createDate: worklistItem.createDate, // "createDate": 1475168601424,
  modifiedDate: worklistItem.modifiedDate, // "modifiedDate": 1475168601424,
  track:
    //worklistItem.jsonTrack &&
    toTrack({
      downloadable: worklistItem.downloadUrl != null,
      ...worklistItem.jsonTrack,
    }),
  //toTrack(worklistItem.jsonTrack.docs[0]._source),
  mergedComponents:
    //toMergedLicenseComponent(worklistItem.mergedComponents),
    //worklistItem.mergedComponents && worklistItem.mergedComponents.map(toMergedLicenseComponent),
    worklistItem.mergedComponents && worklistItem.mergedComponents.map(toMergedLicenseComponent),
  usage: toUsage(worklistItem.usage),
  buyable: !!worklistItem.buyable,
});

const toSimplifiedWorklist = (worklist: any): SimplifiedWorklist => ({
  id: worklist.worklistId, // "worklistId": 201,
  name: worklist.nome, // "nome": "worklist 1",
  isWorklist1: worklist.isWorklist1,
  isWorklist2: worklist.isWorklist2,
});

const toWorklist = (worklist: any): Worklist =>
  Object.assign({}, toSimplifiedWorklist(worklist), {
    coverUrl: keys.API_BASE_URL + worklist.cover, // "cover": "/documents/20205/22617/b80ffa0e-f128-4233-b1e8-078eb38fd7bc.jpg/3ffc1a16-154b-4675-b1ca-ab162a390f14",
    createDate: worklist.createDate, //  "createDate": 1459947307152,
    description: worklist.descrizione, // "descrizione": "descrizione worklist 1",
    modifiedDate: worklist.modifiedDate, //  "modifiedDate": 1459947307152,
    parentWorklistId: worklist.parentWorklistId !== 0 ? worklist.parentWorklistId : undefined, // "parentWorklistId": -1,
    // "permissions": null,
    // sharedWorklist: boolean, //"sharedWorklist": false,
    // ticketId?: number, // "ticketId": -1,
    // "tipoId": 210002,
    usage: worklist.usageBox ? toUsage(worklist.usageBox) : undefined,
    // "usageBoxId": 216,
    uuid: worklist.uuid, // "uuid": "b80ffa0e-f128-4233-b1e8-078eb38fd7bc",
    itemsCount: worklist.worklistItemsCount, // "worklistItemsCount": 0
  });

const toExtendedWorklist = (worklist: any, worklistItems: any): ExtendedWorklist =>
  Object.assign({}, toWorklist(worklist), {
    items: worklistItems.map(toWorklistItem),
  });

const toCartItem = (cartItem: any): CartItem => {
  return {
    id: cartItem.cartItemId,
    description: cartItem.description,
    discountedPrice: cartItem.discountedPrice ? Number(cartItem.discountedPrice) : undefined,
    price: cartItem.price ? Number(cartItem.price) : undefined,
    proservice: cartItem.proserviceCartItem,
    smartlist: cartItem.smartlistCartItem,
    typeId: cartItem.typeId,
    uuid: cartItem.uuid,
    createdDate: cartItem.createDate,
    modifiedDate: cartItem.modifiedDate,
    worklistItem: {
      ...toWorklistItem({
        ...cartItem.worklistItemCartItem.worklistItem,
        buyable: cartItem.worklistItemCartItem.buyable,
        mergedComponents: cartItem.worklistItemCartItem.mergedComponents,
      }),
    },
  };
};

const toMedia = (supporto: any): Media => {
  const hit = supporto;
  const media = {
    id: hit.id, // 79072,
    title: hit.titolo, // titolo: 'The Party (this Is How We Do It)',
    catalogNumber: hit.numeroCatalogo,
    categories:
      hit.categorie &&
      hit.categorie.map(categoria => ({
        id: categoria.id,
        description: categoria.descrizione,
      })),
    label:
      (hit.etichetta && {
        id: hit.etichetta.id,
        name: hit.etichetta.nomePubblico,
      }) ||
      undefined,
    year: hit.anno || undefined,
    availableTracksCount: hit.numeroTracceAvailable,
    availableTracksDuration: hit.durataTotaleTracceAvailable,
    creationDate: hit.dataCreazione,
    covers: [
      toCover(hit.cover, 'SMALL'),
      toCover(hit.cover, 'MEDIUM'),
      toCover(hit.cover, 'LARGE'),
      toCover(hit.cover, 'EXTRALARGE'),
      toCover(hit.cover, 'MEGA'),
    ],
    performers: hit.interpreti && hit.interpreti.map(toPerson),
  };
  return {
    ...media,
    tracks: hit.tracce && hit.tracce.map(toTrackMedia).map(_ => ({ ..._, medias: [media] })),
  };
};

const toTrackMedia = (track: any, index: number): TrackMedia => {
  const hit = track;
  return {
    id: hit.id,
    tags: hit.tags,
    price: hit.price ? Number(hit.price) : undefined,
    bpm: hit.bpm,
    metre: hit.metro,
    orchestration: hit.orchestrazione,
    performers: hit.interpreti ? hit.interpreti.map(toPerson) : undefined,
    authors: hit.autori ? hit.autori.map(toPerson) : undefined,
    duration: hit.durata,
    tempo: hit.tempo,
    genre: hit.genere,
    title: hit.titolo,
    cuts: hit.tagli ? hit.tagli.map(toCut) : undefined,
    downloadable: hit.downloadable,
    usages: hit.utilizzi,
    tones: hit.tonalita,
    opera: hit.opera ? toOpera(hit.opera) : undefined,
    // medias: (hit.supporti || []).map(s => toSimplifiedMedia(s._source || s)), // TODO
    catalog: hit.catalogo ? toCatalog(hit.catalogo) : undefined,
    resources: toAudioResourceSet(hit),
    trackNumber: index + 1,
  };
};

const toSmartlist = (smartlist: any): Smartlist => {
  const result = {
    id: smartlist.smartlistId,
    uuid: smartlist.uuid,
    coverUrl: smartlist.cover,
    demoUrl: smartlist.demo,
    creator: smartlist.creatore,
    description: smartlist.descrizione,
    name: smartlist.nome,
    availableTracksCount: smartlist.numeroElementi,
    parentId: smartlist.parentSmartlistId,
    price: smartlist.price ? Number(smartlist.price) : undefined,
    rank: smartlist.rank,
    unlocked: smartlist.unlocked,
  };
  return result;
};

const toSmartlistItem = (item: any): SmartlistItem => ({
  authors: item.autori, // autori: 'Tricky (Christopher Stewart), Christina Aguilera, Studio Beast (Claude Kelly)',
  createDate: item.createDate, // createDate: 1511886729564,
  duration: item.durata, // durata: 262,
  hdAudioFileId: item.hdAudioFileId, // hdAudioFileId: '',
  sdAudioFileId: item.sdAudioFileId, // sdAudioFileId: '-5pvLhTjYqDrkuESOWL9jA',
  hq: item.hq, // hq: false,
  performers: item.interpreti, // interpreti: 'Christina Aguilera',
  lyricsId: item.lyricsId, // lyricsId: 0,
  modifiedDate: item.modifiedDate, // modifiedDate: 1511886729564,
  number: item.numero, //numero: 2,
  cutsCount: item.numeroTagli, //numeroTagli: 0,
  usagesCount: item.numeroUtilizzi, // numeroUtilizzi: 0,
  price: item.price ? Number(item.price) : undefined, //prezzo: 2.99,
  scoreId: item.scoreId, // scoreId: 0,
  smartlistId: item.smartlistId, // smartlistId: 407,
  id: item.smartlistItemId, // smartlistItemId: 1331,
  media: {
    id: item.supportoId, // supportoId: 31500,
    coverUrl: item.supportoCover, // supportoCover: '',
  },
  title: item.titolo, // titolo: 'Express',
  // tracciaId: 331960,
  track: toTrack(JSON.parse(item.track)),
  uuid: item.uuid, // uuid: '7f2f17dc-6620-47f3-a5f9-0287b7b5d480',
});

const toFeatured = (data: any): Featured => {
  return {
    id: data.id, // "enteId": 208359,
    coverUrl: data.coverUrl, // "cover": "http://localhost:8080.../3ebbe1ff-cb14-407c-aa39-7e690fd9c574",
    name: data.nome, // "nomePubblico": "flipper srl edizioni musicali",
    uuid: data.uuid, //"uuid": "d73cbff7-ccbe-45c2-bc48-df40e0d53b90"
  };
};

const toPurchase = (data: any): Purchase => {
  return {
    description: data.description, // description: 'Powerslave',
    id: data.orderHistoryItemId, // orderHistoryItemId: 301,
    discountedPrice: data.discountedPrice ? Number(data.discountedPrice) : undefined, // price: 2.29,
    price: data.price ? Number(data.price) : undefined, // price: 2.29,
    // proservice : // proservice: null,
    // smartlist : // smartlist: null,
    typeId: data.typeId, // typeId: 230001,
    uuid: data.uuid, // uuid: '98f49446-e4cc-47d2-880c-3afbe043ad98',
    createdDate: data.createDate,
    modifiedDate: data.modifiedDate,
    worklistItem: toWorklistItem({
      ...data.worklistItemOrderHistoryItem.worklistItem,
      mergedComponents: data.worklistItemOrderHistoryItem.mergedComponents,
    }),
  };
};

const toMailRequest = (request: OnDemand): ?MailRequest => {
  let subject = 'New service request';
  let bodyMessage = '';
  const date = moment(request.date).format('YYYY-MM-DD');
  if (request.track) {
    subject = 'New track request';
    bodyMessage = `
      <h1>New track request</h1>
      <p>Title: ${request.track.title}</p>
      <p>Performers: ${request.track.performers}</p>
      <p>Authors: ${request.track.authors}</p>`;
  } else if (request.quotation) {
    subject = 'New quotation request';
    bodyMessage = `
      <h1>New quotation request</h1>
      <p>Title: ${request.quotation.title}</p>`;
  } else if (request.smartlist) {
    subject = 'New smartlist request';
    bodyMessage = `
      <h1>New smartlist request</h1>
      <p>Title: ${request.smartlist.title}</p>`;
  } else {
    subject = 'New help request';
  }
  bodyMessage = `${bodyMessage}
    <hr>
    <h2>${request.title}</h2>
    <h3>${date} (${request.date})</h3>
    <p>${request.note}</p>`;
  return {
    mailRequest: {
      subject,
      bodyMessage,
    },
  };
};

export default {
  toError,
  toCategory,
  toPerson,
  toMedia,
  toSimplifiedMedia,
  toTrack,
  toTrackMedia,
  toCut,
  toAudioResourceSet,
  toOpera,
  toWorkcopies,
  toMediaAggregations,
  toTrackAggregations,
  toSimplifiedUsage,
  toUsage,
  toProduction,
  toProducer,
  toArea,
  toTerritory,
  toUsageType,
  toChannel,
  toBroadcasting,
  toBroadcastType,
  toProductionType,
  toSimplifiedWorklist,
  toWorklist,
  toExtendedWorklist,
  toWorklistItem,
  toCartItem,
  toSmartlist,
  toSmartlistItem,
  toFeatured,
  toPurchase,
  toMailRequest,
};
